import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { useContext, useState } from "react";
import { useParams } from "react-router-dom";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { posGeneralDefault, posGeneralForm, PosGeneralValue } from "./general";

export function usePos() {
  const { teamId } = useParams();
  const rpcClient = useContext(RedoAdminRpcClientContext);

  const [posSettings, setPosSettings] = useState<PosGeneralValue | null>(null);

  const fetchAndSaveSettings = useHandler(async (signal: AbortSignal) => {
    if (!teamId) {
      return;
    }
    const settings = (await rpcClient?.getPosSettings({ teamId }, { signal }))
      ?.settings;

    if (!settings) {
      return null;
    }

    const formattedSettings: PosGeneralValue = {
      defaultPosRestock: settings.defaultPosRestock ?? true,
      chargeAmountPerReturn: settings.chargeAmountPerReturn,
      sendOnlyCompletionEmail: settings.sendOnlyCompletionEmail ?? true,
      allowPOSAdjustments: settings.allowPOSAdjustments ?? true,
      allowPOSManualDiscounts: settings.allowPOSManualDiscounts ?? true,
    };
    setPosSettings(formattedSettings);
    posInput.setValue(formattedSettings);

    return formattedSettings;
  });

  const posSettingsLoad = useLoad(
    async (signal) => {
      await fetchAndSaveSettings(signal);
    },
    [fetchAndSaveSettings],
  );

  const posInput = useInput(posGeneralForm, posSettings || posGeneralDefault);

  const save = useHandler(async (signal: AbortSignal) => {
    if (!teamId) {
      return;
    }
    await rpcClient?.updatePosSettings({
      teamId,
      settings: {
        defaultPosRestock: posInput.value.defaultPosRestock,
        chargeAmountPerReturn: posInput.value.chargeAmountPerReturn,
        sendOnlyCompletionEmail: posInput.value.sendOnlyCompletionEmail,
        allowPOSAdjustments: posInput.value.allowPOSAdjustments,
        allowPOSManualDiscounts: posInput.value.allowPOSManualDiscounts,
      },
    });
    await fetchAndSaveSettings(signal);
  });

  return { posSettingsLoad, save, posInput };
}
